import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Typography,
  TextField,
  Button,
  Container,
  InputAdornment,
  Paper,
  Grid,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  Box
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { WhatsApp as WhatsAppIcon, Check as CheckIcon, ContentCopy as ContentCopyIcon, Phone as PhoneIcon, Dialpad as DialpadIcon, TouchApp as TouchAppIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { toWapi } from '../../util/api';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function WhatsAppChecker(props) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [result, setResult] = useState('');
  const [selectBoxOptions, setSelectBoxOptions] = useState([]);
  const [resultStatus, setResultStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [curlCode, setCurlCode] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  useEffect(() => {
    fetchPhoneList();
  }, [props.product]);

  useEffect(() => {
    setIsButtonDisabled(!selectedPhone || !phoneNumber);
  }, [selectedPhone, phoneNumber]);

  const fetchPhoneList = async () => {
    const { product } = props;
    if (product && product.id && product.settings && product.settings.wapi_token) {
      const { id, settings: { wapi_token } } = product;

      try {
        const data = await toWapi(`/api/${id}/listPhones`, wapi_token);
        if (data && data.status === 200 && Array.isArray(data.data)) {
          const activePhones = data.data.filter(phone => phone.status === "active");
          setSelectBoxOptions(activePhones);
        } else {
          console.log('Data is not in the expected format:', data);
        }
      } catch (err) {
        console.error('Error fetching phone list:', err);
      }
    }
  };

  const handleCheck = async (e) => {
    e.preventDefault();
    const { product } = props;
    const { id, settings: { wapi_token } } = product;

    if (!selectedPhone || !phoneNumber) {
      setResult('Please select a phone and enter a number to check.');
      setResultStatus(null);
      setShowResult(true);
      return;
    }

    setIsLoading(true);
    setResult('');
    setResultStatus(null);
    setShowResult(true);

    try {
      const response = await toWapi(`/api/${id}/${selectedPhone}/checknumberstatus?number=${phoneNumber}@c.us`, wapi_token);

      if (response && response.data && response.data.success === true) {
        if (response.data.result && response.data.result.status === 200) {
          setResult(`The number ${phoneNumber} is using WhatsApp.`);
          setResultStatus(200);
          const curlCommand = `curl -X GET \\
  'https://api.maytapi.com/api/${id}/${selectedPhone}/checknumberstatus?number=${phoneNumber}@c.us' \\
  -H 'x-maytapi-key: ${wapi_token}' \\
  -H 'Content-Type: application/json' \\
  -H 'Accept: application/json'`;

          setCurlCode(curlCommand);
        } else if (response.data.result && response.data.result.status === 404) {
          setResult(`The number ${phoneNumber} is not using WhatsApp.`);
          setResultStatus(404);
        } else {
          setResult(`Unexpected status: ${response.data.result ? response.data.result.status : 'Unknown'}`);
          setResultStatus(null);
        }
      } else {
        setResult('Failed to check the number. Please try again.');
        setResultStatus(null);
        console.error('API response indicates failure:', response);
      }
    } catch (error) {
      console.error('Error checking number:', error);
      setResult('An error occurred while checking the number. Please check your instance status.');
      setResultStatus(null);
    } finally {
      setIsLoading(false);
      setShowResult(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setPhoneNumber(value);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(curlCode);
    setOpenSnackbar(true);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, px: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '1600px' }}>
      <Typography variant="h4" gutterBottom align="left" sx={{ fontWeight: 'bold', mb: 1 }}>
        WhatsApp Number Checker
      </Typography>
      <Typography sx={{ mb: 2 }}>
        The WhatsApp Number Checker is a user-friendly tool designed to verify if a phone number is registered on WhatsApp quickly and effortlessly. To use it, simply select one of your active phone numbers, enter the number you want to check, and click the 'Check Number' button. The tool will promptly process your request and present the result, which you can use for various purposes. This versatile checker provides a simple way to validate WhatsApp numbers.
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Input
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="phone-select-label">Select Active Phone</InputLabel>
              <Select
                labelId="phone-select-label"
                value={selectedPhone}
                onChange={(e) => setSelectedPhone(e.target.value)}
                label="Select Active Phone"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5,
                      width: 250,
                    },
                  },
                }}
              >
                {selectBoxOptions.map((phone) => (
                  <MenuItem key={phone.id} value={phone.id}>
                    {`${phone.id} - ${phone.number}${phone.name ? ` - ${phone.name}` : ''}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              variant="outlined"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              placeholder="Enter a phone number to check"
              InputProps={{
                startAdornment: <InputAdornment position="start">+</InputAdornment>,
              }}
              sx={{ mt: 2 }}
              type="tel"
              inputMode="numeric"
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleCheck}
              disabled={isLoading || isButtonDisabled}
              startIcon={<WhatsAppIcon />}
              sx={{ mt: 2 }}
            >
              {isLoading ? 'Checking...' : 'Check Number'}
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              How to Use?
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Select one of your active phone numbers from the dropdown" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <DialpadIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Enter the phone number you want to check (including country code)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <TouchAppIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Click the 'Check Number' button" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <VisibilityIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="View the result in the section below" />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
      {showResult && (
        <Paper elevation={3} sx={{ mt: 3, p: 3 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
              <CircularProgress />
            </Box>
          ) : result ? (
            <Alert
              severity={resultStatus === 200 ? "success" : "error"}
              sx={{ mb: 2, backgroundColor: resultStatus === 200 ? '#1DE9B6' : '#FF5252', color: '#000' }}
              icon={resultStatus === 200 ? <CheckIcon /> : false}
            >
              {result}
            </Alert>
          ) : null}
          {resultStatus === 200 && !isLoading && (
            <>
              <Typography variant="body1" sx={{ mt: 2, mb: 1, fontWeight: 'medium' }}>
                You can use the cURL code below to get more details about this phone number:
              </Typography>
              <TextField
                multiline
                rows={6}
                fullWidth
                variant="outlined"
                value={curlCode}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={copyToClipboard}
                        edge="end"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
            </>
          )}
        </Paper>
      )}
      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          cURL code copied to clipboard!
        </Alert>
      </Snackbar>
    </Container>
  );
}

const stateToProps = (state) => ({
  access: state.access,
  location: state.router.location,
  product: state.product.maytapi,
  user: state.access.user.loggedUserObj,
});

export default connect(stateToProps)(WhatsAppChecker);